import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiPlus, HiSearch, HiEye, HiPencil, HiTrash,
  HiDocumentText, HiX,
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import { supabase } from '../supabase/client';
import { useAuth } from '../context/AuthContext';
import { formatAmount, formatDate } from '../utils/format';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  Paid:    'bg-green-100  text-green-700',
  Pending: 'bg-yellow-100 text-yellow-700',
  Overdue: 'bg-red-100    text-red-600',
  Draft:   'bg-gray-100   text-gray-500',
};

const FILTERS = ['All', 'Pending', 'Paid', 'Overdue', 'Draft'];

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

function DeleteModal({ invoice, onClose, onConfirm, deleting }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl border border-gray-100 p-6 text-center">
        <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <HiTrash className="w-7 h-7 text-red-500" />
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-2">Delete Invoice</h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          Delete <span className="font-semibold text-gray-800">{invoice.invoice_number}</span>?
          <br />This cannot be undone.
        </p>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 text-sm font-semibold bg-red-500 hover:bg-red-600 text-white rounded-xl transition disabled:opacity-60"
          >
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 px-6 py-4 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-20 flex-shrink-0" />
      <div className="flex-1 h-3.5 bg-gray-100 rounded" />
      <div className="h-4 bg-gray-200 rounded w-16 hidden md:block flex-shrink-0" />
      <div className="h-5 bg-gray-100 rounded-full w-16 hidden md:block flex-shrink-0" />
      <div className="h-3 bg-gray-100 rounded w-20 hidden md:block flex-shrink-0" />
      <div className="flex gap-1">
        <div className="w-8 h-8 bg-gray-100 rounded-lg" />
        <div className="w-8 h-8 bg-gray-100 rounded-lg" />
        <div className="w-8 h-8 bg-gray-100 rounded-lg" />
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Invoices() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const [invoices,     setInvoices]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('invoices')
      .select('*, clients(name)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) toast.error('Failed to load invoices');
    else setInvoices(data ?? []);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleDelete = async () => {
    setDeleting(true);
    const { error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', deleteTarget.id)
      .eq('user_id', user.id);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Invoice deleted');
      setInvoices((prev) => prev.filter((i) => i.id !== deleteTarget.id));
      setDeleteTarget(null);
    }
    setDeleting(false);
  };

  const q = search.toLowerCase();
  const filtered = invoices.filter((inv) => {
    const matchSearch =
      inv.invoice_number.toLowerCase().includes(q) ||
      (inv.clients?.name ?? '').toLowerCase().includes(q);
    const matchStatus = statusFilter === 'All' || inv.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <Layout title="Invoices">

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Invoices</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? 'Loading…' : `${invoices.length} invoice${invoices.length !== 1 ? 's' : ''} total`}
          </p>
        </div>
        <button
          onClick={() => navigate('/invoices/new')}
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white
            text-sm font-semibold px-4 py-2.5 rounded-xl transition shadow-sm self-start sm:self-auto"
        >
          <HiPlus className="w-4 h-4" /> Create Invoice
        </button>
      </div>

      {/* Filters row */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        {/* Search */}
        <div className="relative flex-1">
          <HiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by invoice # or client…"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm
              text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2
              focus:ring-indigo-500 focus:border-transparent"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <HiX className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Status filter pills */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl flex-shrink-0">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition whitespace-nowrap ${
                statusFilter === f
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

        {/* Column headers — md+ */}
        {!loading && filtered.length > 0 && (
          <div className="hidden md:grid px-6 py-3 border-b border-gray-50 bg-gray-50/80
            grid-cols-[130px_minmax(0,1fr)_110px_110px_110px_90px] gap-4">
            {['Invoice #', 'Client', 'Amount', 'Status', 'Due Date', 'Actions'].map((h) => (
              <p key={h} className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{h}</p>
            ))}
          </div>
        )}

        {loading ? (
          <div className="divide-y divide-gray-50">
            {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
          </div>

        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mb-4">
              <HiDocumentText className="w-8 h-8 text-green-400" />
            </div>
            {search || statusFilter !== 'All' ? (
              <>
                <p className="text-sm font-semibold text-gray-700">No invoices match your filter</p>
                <p className="text-xs text-gray-400 mt-1">Try clearing the search or changing the status</p>
                <button
                  onClick={() => { setSearch(''); setStatusFilter('All'); }}
                  className="mt-3 text-xs text-indigo-600 font-medium hover:underline"
                >
                  Clear filters
                </button>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-gray-700">No invoices yet</p>
                <p className="text-xs text-gray-400 mt-1">Create your first invoice to start getting paid</p>
                <button
                  onClick={() => navigate('/invoices/new')}
                  className="mt-4 inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700
                    text-white text-xs font-semibold px-4 py-2 rounded-xl transition"
                >
                  <HiPlus className="w-3.5 h-3.5" /> Create first invoice
                </button>
              </>
            )}
          </div>

        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map((inv) => (
              <div
                key={inv.id}
                className="flex md:grid md:grid-cols-[130px_minmax(0,1fr)_110px_110px_110px_90px]
                  gap-2 md:gap-4 items-center px-6 py-4 hover:bg-gray-50/70 transition group"
              >
                {/* Invoice number */}
                <button
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                  className="text-sm font-bold text-indigo-600 hover:underline text-left"
                >
                  {inv.invoice_number}
                </button>

                {/* Client */}
                <p className="text-sm text-gray-700 truncate flex-1 min-w-0">
                  {inv.clients?.name ?? '—'}
                </p>

                {/* Amount — desktop */}
                <p className="text-sm font-bold text-gray-900 hidden md:block">
                  {formatAmount(inv.amount)}
                </p>

                {/* Status — desktop */}
                <div className="hidden md:block">
                  <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full
                    ${STATUS_STYLE[inv.status] ?? 'bg-gray-100 text-gray-500'}`}>
                    {inv.status}
                  </span>
                </div>

                {/* Due date — desktop */}
                <p className="text-xs text-gray-400 hidden md:block">
                  {formatDate(inv.due_date)}
                </p>

                {/* Mobile: amount + status */}
                <div className="flex md:hidden items-center gap-2 ml-auto flex-shrink-0">
                  <p className="text-sm font-bold text-gray-900">{formatAmount(inv.amount)}</p>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full
                    ${STATUS_STYLE[inv.status] ?? 'bg-gray-100 text-gray-500'}`}>
                    {inv.status}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0
                  md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                    title="View"
                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                  >
                    <HiEye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigate(`/invoices/${inv.id}/edit`)}
                    title="Edit"
                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                  >
                    <HiPencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(inv)}
                    title="Delete"
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Result count hint when filtering */}
      {(search || statusFilter !== 'All') && !loading && filtered.length > 0 && (
        <p className="text-xs text-gray-400 mt-3 px-1">
          Showing {filtered.length} of {invoices.length} invoices
        </p>
      )}

      {deleteTarget && (
        <DeleteModal
          invoice={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}
    </Layout>
  );
}
